import { Message } from "@moe/priv/model/message";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback, useEffect, useRef } from "react";
import { useInfiniteScroll } from "../../hooks/use-infinite-scroll";

/**
 * Scroll manifesto.
 * https://x.com/mayfer/status/1858698456086282583?s=46&t=pd3uh4BnhkMVspxVU9blSQ
 * [x] If the user scrolls to bottom of generated text while still generating, enable auto-scroll.Partially implemented, this is still a bit hacky on mobile
 * [x] Stop auto-scroll if the user manually scrolls up at any point & do not turn it back on until the user manually scrolls to bottom
 * [x] On mobile, stop auto-scroll if the user's finger is touching the screen
 * [ ] If you choose to enable auto-scroll by default, stop auto-scrolling when the start of the response gets to the top of the visible window.
 */

interface UseChatScrollReturn {
  bottomSentinelRef: (node: HTMLElement | null) => void;
  topSentinelRef: (node: HTMLElement | null) => void;
}

interface Props {
  history: Message[];
  historyRef: React.RefObject<Element | null>;
  scrollHeightRef: React.MutableRefObject<number | undefined>;
  isLoading: boolean;
  scrolledToTopHandler: () => void;
  scrolledToBottomHandler?: () => void;
}

export function useChatScroll({
  history,
  isLoading,
  historyRef,
  scrollHeightRef,
  scrolledToTopHandler,
  scrolledToBottomHandler
}: Props): UseChatScrollReturn {
  const shouldAutoScrollRef = useRef(true);
  const isTouchingRef = useRef(false);
  const { flagRef } = useServiceContext();
  const { isDesktopScreen } = useAppContext();

  const { topSentinelRef, bottomSentinelRef } = useInfiniteScroll({
    items: history,
    scrolledToTopHandler,
    scrolledToBottomHandler: () => {
      if (isTouchingRef.current) {
        shouldAutoScrollRef.current = true;
      }
      scrolledToBottomHandler?.();
    }
  });

  const isNearBottom = useCallback((history: Element) => {
    const { scrollTop, scrollHeight, clientHeight } = history;
    return Math.abs(scrollHeight - scrollTop - clientHeight) < 100;
  }, []);

  useEffect(() => {
    const history = historyRef.current;
    if (!history) return;

    const handleWheel = (e: any) => {
      if (!e.isTrusted) return;
      const wheeledUp = e.deltaY < 0;
      if (wheeledUp) {
        shouldAutoScrollRef.current = false;
        return;
      }
      if (isNearBottom(history)) {
        shouldAutoScrollRef.current = true;
      }
    };

    const handleTouchStart = () => {
      isTouchingRef.current = true;
    };

    const handleTouchEnd = () => {
      isTouchingRef.current = false;
    };
    history.addEventListener("touchstart", handleTouchStart, { passive: true });
    history.addEventListener("touchend", handleTouchEnd, { passive: true });
    history.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      history.removeEventListener("touchstart", handleTouchStart);
      history.removeEventListener("touchend", handleTouchEnd);
      history.removeEventListener("wheel", handleWheel);
    };
  }, [historyRef, isNearBottom]);

  // Scroll to bottom on load
  useEffect(() => {
    // We don't want to scroll if the user just started the chat with only a greeting message
    // This is better UX because the user wants to reading the greeting first
    if (history.length <= 1) return;
    if (historyRef.current) historyRef.current.scrollTop = historyRef.current.scrollHeight;
  }, [history.length, historyRef, isLoading]);

  useEffect(() => {
    const { sent, replied, fetchedPreviousPage, newChunk } = flagRef.current;
    const history = historyRef.current;
    if (!history || isLoading) return;

    // Restore old scroll position on fetching previous page
    if (fetchedPreviousPage && scrollHeightRef.current) {
      history.scrollTop = history.scrollHeight - scrollHeightRef.current;
      scrollHeightRef.current = undefined;
      flagRef.current.fetchedPreviousPage = false;
      return;
    }
  }, [flagRef, history, historyRef, isLoading, scrollHeightRef]);

  /**
   * Manage scroll positions
   */
  useEffect(() => {
    // We need to know two things to correctly manage scroll:
    // 1. When the user has performed a relevant action i.e sent a message, scrolled to top, etc
    // 2. when the mutations the user triggered *through* the action has rendered to the screen.
    // This ref lets us know about 2. And the useEffect() below lets us know about 1.
    const { sent, replied, fetchedPreviousPage, newChunk } = flagRef.current;

    const history = historyRef.current;
    if (!history) return;

    // Scroll to bottom when a message is sent
    if (sent) {
      history.scrollTo({ top: history.scrollHeight, behavior: "instant" });
      flagRef.current.sent = false;
      flagRef.current.replied = false;
      return;
    }

    if (replied) {
      history.scrollTo({ top: history.scrollHeight, behavior: "instant" });
      flagRef.current.replied = false;
      return;
    }

    // When a new chunk is received, scroll to bottom if the user is already at the bottom
    if (newChunk && shouldAutoScrollRef.current) {
      // Don't scroll if user is touching the screen
      if (!isDesktopScreen && isTouchingRef.current) {
        return;
      }

      if (isNearBottom(history)) {
        history.scrollTo({ top: history.scrollHeight, behavior: "instant" });
      }
      flagRef.current.newChunk = false;
      return;
    }
  }, [flagRef, history, historyRef, isDesktopScreen, isNearBottom, scrollHeightRef]);

  return {
    bottomSentinelRef,
    topSentinelRef
  };
}
